import { DownOutlined } from "@ant-design/icons";
import { Button, Dropdown } from "antd";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import "./emailEditor.css";
import {
  MERGE_FIELD_BLOT,
  opsToSubject,
  registerEmailFormats,
  subjectToOps,
  typeBesideChips,
} from "./quillEmailSetup";

registerEmailFormats();

const Delta = Quill.import("delta");

// Pasted or dropped text can bring line breaks with it; a subject is a single line
const flattenLineBreaks = (quill) => {
  const breaks = [];
  let index = 0;
  quill.getContents().ops.forEach((op) => {
    if (typeof op.insert === "string") {
      let at = op.insert.indexOf("\n");
      while (at !== -1) {
        breaks.push(index + at);
        at = op.insert.indexOf("\n", at + 1);
      }
      index += op.insert.length;
    } else {
      index += 1;
    }
  });
  breaks.pop(); // Quill's own closing line break
  if (!breaks.length) return;

  const delta = new Delta();
  breaks.forEach((at, i) => {
    delta
      .retain(at - (i ? breaks[i - 1] + 1 : 0))
      .delete(1)
      .insert(" ");
  });
  quill.updateContents(delta, "api");
};

/*
  A one-line subject box that shows merge fields as the same chips as the email body.

  defaultValue  subject text with {{tokens}}, loaded once on mount (remount with a `key`)
  label         accessible name of the box
  onChange      called with the subject text, chips written back as {{tokens}}
  fields        merge fields offered by "Insert field"
  ref           { getText(), insertField(key), focus() }
*/
const EmailSubjectInput = forwardRef(
  (
    {
      defaultValue,
      onChange,
      fields = [],
      placeholder = "Subject line",
      label = "Subject",
    },
    ref,
  ) => {
    const hostRef = useRef(null);
    const quillRef = useRef(null);
    const onChangeRef = useRef(onChange);

    useLayoutEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      const host = hostRef.current;
      if (!host) return undefined;

      const quill = new Quill(host.appendChild(document.createElement("div")), {
        placeholder,
        formats: [MERGE_FIELD_BLOT],
        modules: {
          clipboard: { matchVisual: false },
          // A pasted image has nowhere to go in a subject line
          uploader: { mimetypes: [] },
          keyboard: {
            bindings: {
              // Tab moves on to the next control instead of typing a tab
              tab: null,
              noLineBreaks: {
                key: "Enter",
                shiftKey: null,
                handler: () => false,
              },
            },
          },
        },
      });
      quillRef.current = quill;
      quill.root.setAttribute("role", "textbox");
      quill.root.setAttribute("aria-multiline", "false");
      quill.root.setAttribute("aria-label", label);

      typeBesideChips(quill);

      quill.setContents(subjectToOps(defaultValue, fields), "api");
      quill.history.clear();

      const handleTextChange = (_delta, _oldDelta, source) => {
        if (source !== "user") return;
        flattenLineBreaks(quill);
        onChangeRef.current?.(opsToSubject(quill.getContents().ops));
      };
      quill.on(Quill.events.TEXT_CHANGE, handleTextChange);

      return () => {
        quill.off(Quill.events.TEXT_CHANGE, handleTextChange);
        quillRef.current = null;
        host.innerHTML = "";
      };
      // The editor is created once; new content means a new `key` on the component
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const insertField = (key) => {
      const quill = quillRef.current;
      const field = fields.find((candidate) => candidate.key === key);
      if (!quill || !field) return;

      const range = quill.getSelection(true);
      if (range.length > 0) quill.deleteText(range.index, range.length, "user");
      quill.insertEmbed(
        range.index,
        MERGE_FIELD_BLOT,
        { key: field.key, label: field.label },
        "user",
      );
      quill.setSelection(range.index + 1, 0, "user");
    };

    useImperativeHandle(ref, () => ({
      getText: () => opsToSubject(quillRef.current?.getContents().ops),
      insertField,
      focus: () => quillRef.current?.focus(),
    }));

    return (
      <div className="email-subject">
        <div ref={hostRef} className="email-subject-input" />
        {fields.length > 0 && (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: fields.map((field) => ({
                key: field.key,
                label: field.label,
                title: field.description,
              })),
              onClick: ({ key }) => insertField(key),
            }}
          >
            <Button className="email-subject-button">
              Insert field <DownOutlined />
            </Button>
          </Dropdown>
        )}
      </div>
    );
  },
);

EmailSubjectInput.displayName = "EmailSubjectInput";

export default EmailSubjectInput;
